import _isEmpty from 'lodash/isEmpty';
import _set from 'lodash/fp/set';

import {
  getSavedSelectedPresetForPage,
  saveSelectedPresetForPage,
  getSavedActiveSavedViewForPage,
  saveActiveSavedViewForPage,
  saveSavedViewConfigForPage,
  getSavedViewConfigForPage,
} from 'utils/localStorage';
import { getDefaultColumns, PRESET_OPTIONS } from 'consts/dashboard';
import type { Page } from 'consts/pages';

import type { StoreSet } from '../index';
import { INITIAL_CONFIG, type DashboardConfig, type Widget } from './config';
import { migrate } from './migrations';
import { getColumnsConfig, getPageSelectedPresetName, getPageSelectedPresetConfiguration } from './selectors';
import { setColumnsConfig, mapColumnsConfig, setPagePresetConfig, addMissingDefaultPresets } from './utils';

type SavedView = { name: string; widgets?: object[]; labels?: string[] };

export type DashboardConfigurationSlice = {
  isLoading: boolean;
  config: DashboardConfig;
  isApplyingView: boolean;
  activeSavedView: Record<string, string | null>;
  preservedCustomConfig: Record<string, unknown>;
  sensorProfileTypes: string[];
  selectedPreset: Record<string, string>;
  setLoading: (isLoading: boolean) => void;
  setInitialDashboardConfig: (params: { config: string; sensorProfileTypes: string[] }) => void;
  setWidgetPropForSelectedPreset: (page: string, index: number, prop: string) => void;
  setWidgetTypeForSelectedPreset: (page: string, index: number, type: string) => void;
  removeWidgetFromSelectedPreset: (page: string, index: number) => void;
  addWidgetToSelectedPreset: (page: string, type: string, dataKey: string) => void;
  setTimechartBreakdown: (page: string, breakdownField: string | null) => void;
  applySavedView: (page: string, view: SavedView) => void;
  clearActiveSavedView: (page: string) => void;
  restoreCustomConfig: (page: string) => void;
  setActiveSavedView: (page: string, name: string) => void;
  removeColumn: (page: string, property: string, log?: string) => void;
  addColumn: (page: string, property: string, log?: string) => void;
  resetColumns: (page: string, log?: string) => void;
  reorderColumns: (page: string, log: string, sourceIndex: number, destinationIndex: number) => void;
  persistLayout: (id: string, newLayout: object) => void;
  setSelectedPreset: (page: string, preset: string) => void;
  resetSelectedPreset: (page: string) => void;
  removeInvalidWidgetsFromSelectedPreset: (propList: string[], page: string) => void;
};

const createDashboardConfiguration = (set: StoreSet): DashboardConfigurationSlice => ({
  isLoading: true,
  config: INITIAL_CONFIG,
  isApplyingView: false,
  activeSavedView: Object.keys(INITIAL_CONFIG.presets).reduce<Record<string, string | null>>(
    (activeSavedViewMap, page) => {
      const savedView = getSavedActiveSavedViewForPage(page);
      return { ...activeSavedViewMap, [page]: savedView ?? null };
    },
    {},
  ),
  preservedCustomConfig: {},
  sensorProfileTypes: [],
  selectedPreset: Object.keys(INITIAL_CONFIG.presets).reduce<Record<string, string>>((selectedPresetMap, page) => {
    const savedPreset = getSavedSelectedPresetForPage(page) || 'custom';
    return { ...selectedPresetMap, [page]: savedPreset };
  }, {}),
  setLoading: (isLoading) => {
    set((prev) => ({
      ...prev,
      dashboardConfiguration: {
        ...prev.dashboardConfiguration,
        isLoading,
      },
    }));
  },
  setInitialDashboardConfig: ({ config, sensorProfileTypes }) => {
    const parsedConfig = JSON.parse(config) as Partial<DashboardConfig>;

    let newConfig = addMissingDefaultPresets(
      _isEmpty(parsedConfig)
        ? { ...INITIAL_CONFIG, columnsConfig: mapColumnsConfig(INITIAL_CONFIG) }
        : (migrate({ ...parsedConfig, columnsConfig: mapColumnsConfig(parsedConfig) }) as DashboardConfig),
    );

    Object.keys(newConfig.presets).forEach((page) => {
      const activeSavedView = getSavedActiveSavedViewForPage(page);

      if (activeSavedView) {
        const savedViewConfig = getSavedViewConfigForPage(page) as
          | { widgets?: Widget[]; breakdown?: string | null }
          | undefined;
        if (savedViewConfig && savedViewConfig.widgets) {
          newConfig = _set(`presets.${page}.custom`, savedViewConfig.widgets, newConfig);
          if (savedViewConfig.breakdown !== undefined) {
            newConfig = _set(`timechartBreakdown.${page}`, savedViewConfig.breakdown, newConfig);
          }
        }
      }
    });

    set((prev) => ({
      ...prev,
      dashboardConfiguration: {
        ...prev.dashboardConfiguration,
        isLoading: false,
        config: newConfig,
        sensorProfileTypes,
      },
    }));
  },

  setWidgetPropForSelectedPreset: (page, index, prop) => {
    set((state) => {
      const selectedPagePreset = getPageSelectedPresetName(state, page);
      const selectedPagePresetsConfig = getPageSelectedPresetConfiguration(state, page);

      const newConfig = selectedPagePresetsConfig.map((conf, i) =>
        i === index ? { type: selectedPagePresetsConfig[index].type, dataKey: prop } : conf,
      );

      return setPagePresetConfig(state, page, selectedPagePreset, newConfig);
    });
  },
  setWidgetTypeForSelectedPreset: (page, index, type) => {
    set((state) => {
      const selectedPagePreset = getPageSelectedPresetName(state, page);
      const selectedPagePresetsConfig = getPageSelectedPresetConfiguration(state, page);

      const newConfig = selectedPagePresetsConfig.map((conf, i) =>
        i === index ? { type: type, dataKey: selectedPagePresetsConfig[index].dataKey } : conf,
      );

      return setPagePresetConfig(state, page, selectedPagePreset, newConfig);
    });
  },
  removeWidgetFromSelectedPreset: (page, index) => {
    set((state) => {
      const selectedPagePreset = getPageSelectedPresetName(state, page);
      const selectedPagePresetsConfig = getPageSelectedPresetConfiguration(state, page);
      const newConfig = selectedPagePresetsConfig.slice(0, index).concat(selectedPagePresetsConfig.slice(index + 1));
      return setPagePresetConfig(state, page, selectedPagePreset, newConfig);
    });
  },
  addWidgetToSelectedPreset: (page, type, dataKey) => {
    set((state) => {
      const selectedPagePreset = getPageSelectedPresetName(state, page);
      const selectedPagePresetsConfig = getPageSelectedPresetConfiguration(state, page);
      const newConfig = selectedPagePresetsConfig.concat({ type, dataKey });

      return setPagePresetConfig(state, page, selectedPagePreset, newConfig);
    });
  },

  setTimechartBreakdown: (page, breakdownField) => {
    set((state) => _set(`dashboardConfiguration.config.timechartBreakdown.${page}`, breakdownField, state));
  },

  applySavedView: (page, view) => {
    set((state) => {
      const targetPreset = 'custom';

      const currentCustomConfig = state.dashboardConfiguration.config.presets[page].custom;
      let newState = _set(`dashboardConfiguration.preservedCustomConfig.${page}`, currentCustomConfig, state);

      newState = setPagePresetConfig(newState, page, targetPreset, (view.widgets ?? []) as Widget[]);

      const breakdownLabel = view.labels?.find((label) => label.startsWith('breakdown:'));
      const breakdown = breakdownLabel ? breakdownLabel.replace('breakdown:', '') : null;

      newState = _set(`dashboardConfiguration.config.timechartBreakdown.${page}`, breakdown, newState);
      newState = _set(`dashboardConfiguration.selectedPreset.${page}`, targetPreset, newState);
      newState = _set(`dashboardConfiguration.activeSavedView.${page}`, view.name, newState);
      newState = _set(`dashboardConfiguration.isApplyingView`, true, newState);

      saveActiveSavedViewForPage(page, view.name);
      saveSavedViewConfigForPage(page, { widgets: view.widgets || [], breakdown });
      saveSelectedPresetForPage(page, targetPreset);

      setTimeout(() => {
        set((state) => _set(`dashboardConfiguration.isApplyingView`, false, state));
      }, 100);

      return newState;
    });
  },

  clearActiveSavedView: (page) => {
    set((state) => _set(`dashboardConfiguration.activeSavedView.${page}`, null, state));
    saveActiveSavedViewForPage(page, null);
    saveSavedViewConfigForPage(page, null);
  },
  restoreCustomConfig: (page) => {
    set((state) => {
      const preservedConfig = state.dashboardConfiguration.preservedCustomConfig?.[page];
      if (preservedConfig) {
        return setPagePresetConfig(state, page, 'custom', preservedConfig as Widget[]);
      }
      return state;
    });
  },
  setActiveSavedView: (page, name) => {
    set((state) => _set(`dashboardConfiguration.activeSavedView.${page}`, name, state));
    saveActiveSavedViewForPage(page, name);
  },

  removeColumn: (page, property, log) => {
    set((state) => {
      const columns = getColumnsConfig(state, page, log).filter((k) => k !== property);

      return setColumnsConfig(state, page, log, columns);
    });
  },
  addColumn: (page, property, log) => {
    set((state) => {
      const columns = getColumnsConfig(state, page, log).concat(property);

      return setColumnsConfig(state, page, log, columns);
    });
  },
  resetColumns: (page, log) => {
    set((state) => setColumnsConfig(state, page, log, getDefaultColumns({ page, log })));
  },
  reorderColumns: (page, log, sourceIndex, destinationIndex) => {
    set((state) => {
      const columnsConfig = getColumnsConfig(state, page, log);

      const copiedColumnsConfig = [...columnsConfig];
      const [removed] = copiedColumnsConfig.splice(sourceIndex, 1);
      copiedColumnsConfig.splice(destinationIndex, 0, removed);

      return setColumnsConfig(state, page, log, copiedColumnsConfig);
    });
  },
  persistLayout: (id, newLayout) => {
    set((state) => {
      const { customDashboards } = state.dashboardConfiguration.config;
      const newDashboardConfig = { id, layout: newLayout };

      const configToPersist = customDashboards.find((c) => c.id === id)
        ? customDashboards.map((c) => (c.id === id ? newDashboardConfig : c))
        : customDashboards.concat(newDashboardConfig);

      return _set(`dashboardConfiguration.config.customDashboards`, configToPersist, state);
    });
  },

  setSelectedPreset: (page, preset) => {
    set((state) => _set(`dashboardConfiguration.selectedPreset.${page}`, preset, state));
    saveSelectedPresetForPage(page, preset);
  },
  resetSelectedPreset: (page) => {
    let selectedPreset: string | undefined;
    set((state) => {
      selectedPreset = getPageSelectedPresetName(state, page);

      const presetDefaultWidgets = PRESET_OPTIONS[page as Page]!.find(
        (p) => p.value === selectedPreset,
      )!.defaultWidgets;

      return _set(`dashboardConfiguration.config.presets.${page}.${selectedPreset}`, presetDefaultWidgets, state);
    });

    saveSelectedPresetForPage(page, selectedPreset as string);
  },
  removeInvalidWidgetsFromSelectedPreset: (propList, page) => {
    set((state) => {
      const selectedPreset = getPageSelectedPresetName(state, page);
      const widgetConfig = getPageSelectedPresetConfiguration(state, page);

      const defaultWidgetsForPage = PRESET_OPTIONS[page as Page]!.map((p) => p.defaultWidgets).flat();

      const updatedConfig = widgetConfig.filter((widget) => {
        if (defaultWidgetsForPage.some((defaultWidget) => defaultWidget.dataKey === widget.dataKey)) {
          return true;
        }

        return propList.includes(widget.dataKey);
      });

      if (widgetConfig.length !== updatedConfig.length) {
        return _set(`dashboardConfiguration.config.presets.${page}.${selectedPreset}`, updatedConfig, state);
      }

      return state;
    });
  },
});

export default createDashboardConfiguration;
